import ResponsiveTable from '../../components/ResponsiveTable';
import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faSave, faClock, faCalendarAlt, faPlus, faTrash, faBoxes, faBell } from '@fortawesome/free-solid-svg-icons';
import OrgOptionsManager from '../../components/OrgOptionsManager';
import '../../styles/App.css';

const AdminSettings = () => {
    const [loading, setLoading] = useState(true);
    const [holidays, setHolidays] = useState([]);
    
    const [settings, setSettings] = useState({
        dayShiftStartTime: '09:30',
        dayShiftEndTime: '18:30',
        nightShiftStartTime: '19:00',
        nightShiftEndTime: '04:00',
        gracePeriod: 15,
        halfDayThreshold: 30,
        inventoryCatAThreshold: 500, // 👇 Default State
        inventoryCatBThreshold: 100, // 👇 Default State
        taskDueSoonMinutes: 15
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [settingsRes, holidaysRes] = await Promise.all([
                api.get('/settings'),
                api.get('/holidays')
            ]);

            if (settingsRes.data) {
                setSettings({
                    dayShiftStartTime: settingsRes.data.dayShiftStartTime || '09:30',
                    dayShiftEndTime: settingsRes.data.dayShiftEndTime || '18:30',
                    nightShiftStartTime: settingsRes.data.nightShiftStartTime || '19:00',
                    nightShiftEndTime: settingsRes.data.nightShiftEndTime || '04:00',
                    gracePeriod: settingsRes.data.gracePeriod || 15,
                    halfDayThreshold: settingsRes.data.halfDayThreshold || 30,
                    inventoryCatAThreshold: settingsRes.data.inventoryCatAThreshold || 500, // 👇 Fetch
                    inventoryCatBThreshold: settingsRes.data.inventoryCatBThreshold || 100, // 👇 Fetch
                    // ?? not ||: 0 is a real value here (reminder off)
                    taskDueSoonMinutes: settingsRes.data.taskDueSoonMinutes ?? 15
                });
            }
            setHolidays(holidaysRes.data || []);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching data", err);
            setLoading(false);
        }
    };

    const handleSaveSettings = async (e) => {
        e.preventDefault();
        try {
            await api.put('/settings', settings);
            Swal.fire({
                title: 'Settings Saved!',
                text: 'System rules have been updated.',
                icon: 'success',
                confirmButtonColor: '#215D7B'
            });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Failed to update settings.', 'error');
        }
    };

    const handleAddHoliday = async () => {
        const { value: formValues } = await Swal.fire({
            title: 'Add New Holiday',
            html: `
                <input id="holiday-name" class="swal2-input" placeholder="Holiday Name (e.g. Diwali)">
                <input id="holiday-date" type="date" class="swal2-input">
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Add Holiday',
            confirmButtonColor: '#215D7B',
            preConfirm: () => {
                return {
                    name: document.getElementById('holiday-name').value,
                    date: document.getElementById('holiday-date').value
                };
            }
        });

        if (formValues && formValues.name && formValues.date) {
            try {
                await api.post('/holidays', formValues);
                Swal.fire('Success', 'Holiday added successfully', 'success');
                const res = await api.get('/holidays');
                setHolidays(res.data);
            } catch (err) {
                Swal.fire('Error', 'Failed to add holiday', 'error');
            }
        }
    };

    const handleDeleteHoliday = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Holiday?',
            text: "This will remove it from everyone's calendar.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/holidays/${id}`);
                Swal.fire('Deleted', 'Holiday has been removed.', 'success');
                setHolidays(holidays.filter(h => h._id !== id));
            } catch (err) {
                Swal.fire('Error', 'Failed to delete holiday', 'error');
            }
        }
    };

    if (loading) return <div className="main-content">Loading Configuration...</div>;

    return (
        <div className="settings-container" style={{ width: '100%', paddingBottom: '40px' }}>
            <h1 className="page-title"><FontAwesomeIcon icon={faCog} /> System Configuration</h1>

            {/* CARD 1: ATTENDANCE RULES */}
            <div className="control-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ flex: '0 0 280px' }}>
                    <h3 style={{ margin: 0, color: 'light-dark(#215D7B, var(--theme-accent-ink))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FontAwesomeIcon icon={faClock} /> Attendance Rules
                    </h3>
                    <p style={{ color: 'light-dark(#777, var(--theme-muted, #959ba5))', fontSize: '14px', margin: '10px 0 0', lineHeight: '1.6' }}>
                        Configure office timings for Day and Night shifts, along with auto-marking logic.
                    </p>
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <form onSubmit={handleSaveSettings}>
                        <div className="settings-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '15px' }}>
                            <div className="form-group">
                                <label className="input-label" style={{color: 'light-dark(#e67e22, var(--theme-warning-ink, #f0c27a))'}}><FontAwesomeIcon icon={faClock} /> Day Shift Start</label>
                                <input type="time" className="swal2-input custom-input" value={settings.dayShiftStartTime} onChange={(e) => setSettings({ ...settings, dayShiftStartTime: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="input-label" style={{color: 'light-dark(#e67e22, var(--theme-warning-ink, #f0c27a))'}}>Day Shift End</label>
                                <input type="time" className="swal2-input custom-input" value={settings.dayShiftEndTime} onChange={(e) => setSettings({ ...settings, dayShiftEndTime: e.target.value })} />
                            </div>
                        </div>

                        <div className="settings-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px', paddingBottom: '20px', borderBottom: '1px solid light-dark(#eee, var(--theme-border, #33363b))' }}>
                            <div className="form-group">
                                <label className="input-label" style={{color: 'light-dark(#2980b9, var(--theme-accent-ink, #78c2e5))'}}><FontAwesomeIcon icon={faClock} /> Night Shift Start</label>
                                <input type="time" className="swal2-input custom-input" value={settings.nightShiftStartTime} onChange={(e) => setSettings({ ...settings, nightShiftStartTime: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <label className="input-label" style={{color: 'light-dark(#2980b9, var(--theme-accent-ink, #78c2e5))'}}>Night Shift End</label>
                                <input type="time" className="swal2-input custom-input" value={settings.nightShiftEndTime} onChange={(e) => setSettings({ ...settings, nightShiftEndTime: e.target.value })} />
                            </div>
                        </div>

                        <div className="settings-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                            <div className="form-group">
                                <label className="input-label">Grace Period (Mins)</label>
                                <input type="number" className="swal2-input custom-input" value={settings.gracePeriod} onChange={(e) => setSettings({ ...settings, gracePeriod: e.target.value })} />
                                <small className="hint-text">Delay allowed before marking "Late"</small>
                            </div>
                            <div className="form-group">
                                <label className="input-label">Half-Day Threshold (Mins)</label>
                                <input type="number" className="swal2-input custom-input" value={settings.halfDayThreshold} onChange={(e) => setSettings({ ...settings, halfDayThreshold: e.target.value })} />
                                <small className="hint-text">Delay triggering "Half Day"</small>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button type="submit" className="gts-btn primary" style={{ padding: '10px 30px', fontSize: '14px', minWidth: '200px' }}>
                                <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} /> Save Rules
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* 👇 NEW: CARD 2 - INVENTORY CATEGORIES */}
            <div className="control-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ flex: '0 0 280px' }}>
                    <h3 style={{ margin: 0, color: 'light-dark(#215D7B, var(--theme-accent-ink))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FontAwesomeIcon icon={faBoxes} /> Inventory Categories
                    </h3>
                    <p style={{ color: 'light-dark(#777, var(--theme-muted, #959ba5))', fontSize: '14px', margin: '10px 0 0', lineHeight: '1.6' }}>
                        Define the price thresholds for inventory classification. <strong style={{color: 'light-dark(#dc2626, var(--theme-danger-ink))'}}>Category A</strong> requires image proofs when logging expenses.
                    </p>
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <form onSubmit={handleSaveSettings}>
                        <div className="settings-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                            <div className="form-group">
                                <label className="input-label" style={{color: 'light-dark(#dc2626, var(--theme-danger-ink))'}}>Category A Minimum (₹)</label>
                                <input type="number" className="swal2-input custom-input" value={settings.inventoryCatAThreshold} onChange={(e) => setSettings({ ...settings, inventoryCatAThreshold: Number(e.target.value) })} />
                                <small className="hint-text">Value for highly expensive items (e.g., 500)</small>
                            </div>
                            <div className="form-group">
                                <label className="input-label" style={{color: 'light-dark(#f59e0b, var(--theme-warning-ink, #f0c27a))'}}>Category B Minimum (₹)</label>
                                <input type="number" className="swal2-input custom-input" value={settings.inventoryCatBThreshold} onChange={(e) => setSettings({ ...settings, inventoryCatBThreshold: Number(e.target.value) })} />
                                <small className="hint-text">Mid-tier items (Cat C will be anything below this)</small>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button type="submit" className="gts-btn primary" style={{ padding: '10px 30px', fontSize: '14px', minWidth: '200px' }}>
                                <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} /> Save Thresholds
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* CARD: TASK REMINDERS */}
            <div className="control-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div style={{ flex: '0 0 280px' }}>
                    <h3 style={{ margin: 0, color: 'light-dark(#215D7B, var(--theme-accent-ink))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FontAwesomeIcon icon={faBell} /> Task Reminders
                    </h3>
                    <p style={{ color: 'light-dark(#777, var(--theme-muted, #959ba5))', fontSize: '14px', margin: '10px 0 0', lineHeight: '1.6' }}>
                        Assignees get a notification in the app and on the web shortly before a task goes overdue. It is not sent on WhatsApp.
                    </p>
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <form onSubmit={handleSaveSettings}>
                        <div className="settings-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                            <div className="form-group">
                                <label className="input-label" htmlFor="task-due-soon-minutes">Remind Before Deadline (Mins)</label>
                                <input
                                    id="task-due-soon-minutes"
                                    type="number"
                                    min="0"
                                    max="240"
                                    step="1"
                                    required
                                    className="swal2-input custom-input"
                                    value={settings.taskDueSoonMinutes}
                                    onChange={(e) => setSettings({ ...settings, taskDueSoonMinutes: e.target.value })}
                                />
                                <small className="hint-text">
                                    {Number(settings.taskDueSoonMinutes) > 0
                                        ? `Sent ${Number(settings.taskDueSoonMinutes)} minutes before the task is due. Use 0 to turn it off.`
                                        : 'Reminders are off. Enter a number of minutes (up to 240) to turn them on.'}
                                </small>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button type="submit" className="gts-btn primary" style={{ padding: '10px 30px', fontSize: '14px', minWidth: '200px' }}>
                                <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} /> Save Reminder
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            {/* CARD: DEPARTMENTS & DESIGNATIONS */}
            <OrgOptionsManager />

            {/* CARD 3: HOLIDAY MANAGEMENT */}
            <div className="control-card" style={{ display: 'flex', flexWrap: 'wrap', gap: '40px', alignItems: 'center' }}>
                <div style={{ flex: '0 0 280px' }}>
                    <h3 style={{ margin: 0, color: 'light-dark(#215D7B, var(--theme-accent-ink))', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} /> Holiday Management
                    </h3>
                    <p style={{ color: 'light-dark(#777, var(--theme-muted, #959ba5))', fontSize: '14px', margin: '10px 0 20px', lineHeight: '1.6' }}>
                        Manage company-wide holidays.
                    </p>
                    <button className="gts-btn primary" onClick={handleAddHoliday} style={{ width: '100%', fontSize: '14px', padding: '10px' }}>
                        <FontAwesomeIcon icon={faPlus} style={{ marginRight: '5px' }} /> Add Holiday
                    </button>
                </div>

                <div style={{ flex: '1', minWidth: '300px' }}>
                    <div className="admin-table-wrapper" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <ResponsiveTable className="holiday-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: 'light-dark(#f8f9fa, var(--theme-surface-soft, #202327))', textAlign: 'left', fontSize: '13px', textTransform: 'uppercase', color: 'light-dark(#64748b, var(--theme-muted))' }}>
                                    <th style={{ padding: '12px', borderBottom: '2px solid light-dark(#eef2f6, var(--theme-border, #33363b))' }}>Date</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid light-dark(#eef2f6, var(--theme-border, #33363b))' }}>Holiday Name</th>
                                    <th style={{ padding: '12px', borderBottom: '2px solid light-dark(#eef2f6, var(--theme-border, #33363b))', textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {holidays.length === 0 ? (
                                    <tr>
                                        <td colSpan="3" style={{ textAlign: 'center', padding: '30px', color: 'light-dark(#999, var(--theme-subtle, #7a808a))' }}>
                                            No holidays added yet.
                                        </td>
                                    </tr>
                                ) : (
                                    holidays.map(holiday => (
                                        <tr key={holiday._id} style={{ borderBottom: '1px solid light-dark(#f1f1f1, var(--theme-border, #33363b))' }}>
                                            <td style={{ padding: '14px 12px', fontSize: '14px', color: 'light-dark(#333, var(--theme-ink, #dcdfe4))' }}>
                                                {new Date(holiday.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                            </td>
                                            <td style={{ padding: '14px 12px', fontWeight: '500', color: 'light-dark(#333, var(--theme-ink, #dcdfe4))' }}>
                                                {holiday.name}
                                            </td>
                                            <td style={{ padding: '14px 12px', textAlign: 'right' }}>
                                                <button className="gts-btn danger" style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => handleDeleteHoliday(holiday._id)} title="Delete Holiday">
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </ResponsiveTable>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminSettings;